import { z } from 'zod';
import { Toast, ToastVariant, useToast } from '@hungryroot/components-toast';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import { Warning } from '@hungryroot/ui-kit';

const errorSchema = z.object({
  msg_id: z.string().nullable().optional(),
  /**
   * The cart endpoints return `detail` as a JSON-encoded string holding a DRF
   * field-error map, but they don't all agree — some send the map itself, and
   * some send a bare sentence. Take it as `unknown` and normalize below, so one
   * endpoint's shape can't fail the parse for every other branch here.
   */
  detail: z.unknown(),
});

/** A DRF field-error map: `{ product_id: ['…'] }` or `{ pairing: { id: ['…'] } }`. */
const fieldErrorsSchema = z.record(z.string(), z.unknown());

/**
 * Reduces `detail` to a field-error map, whether it arrived as one or as a
 * JSON-encoded string of one. A bare sentence (`"No Bundle matches the given
 * query."`) or a bare list is not a field-error map and yields `null`.
 */
function parseDetail(detail: unknown): Record<string, unknown> | null {
  if (typeof detail === 'string') {
    try {
      return parseDetail(JSON.parse(detail));
    } catch {
      return null;
    }
  }

  const parsed = fieldErrorsSchema.safeParse(detail);
  return parsed.success ? parsed.data : null;
}

function stringsIn(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * The messages DRF nested under `field`, flattening the two shapes the cart
 * endpoints use: `{ pairing: ['…'] }` and `{ pairing: { id: ['…'] } }`.
 */
function fieldMessages(
  detail: Record<string, unknown>,
  field: string
): string[] {
  const value = detail[field];
  const direct = stringsIn(value);
  if (direct.length > 0) {
    return direct;
  }

  const nested = fieldErrorsSchema.safeParse(value);
  if (!nested.success) {
    return [];
  }
  return Object.values(nested.data).flatMap(stringsIn);
}

/** Matches the inventory wording and the wording the API actually sends. */
function isUnavailable(message: string): boolean {
  return (
    message.includes('out of stock') ||
    message.includes('currently unavailable')
  );
}

/**
 * The customer-facing message for a cart failure we recognize, or `null` when
 * the failure is one we can only describe generically.
 */
function getToastMessage(error: z.infer<typeof errorSchema>): string | null {
  const { msg_id: msgId } = error;
  if (msgId === 'cold_volume_exceeded') {
    return 'Exceeded cold volume limit';
  }
  if (msgId === 'total_volume_exceeded') {
    return 'Exceeded total box volume limit';
  }

  const detail = parseDetail(error.detail);
  if (detail === null) {
    return null;
  }

  const pairingMessages = fieldMessages(detail, 'pairing');
  if (pairingMessages.some(isUnavailable)) {
    return 'One or more items are out of stock';
  }
  if (
    pairingMessages.some((message) => message.includes('Invalid or inactive'))
  ) {
    return 'Recipe is currently unavailable';
  }

  if (fieldMessages(detail, 'product_id').some(isUnavailable)) {
    return 'This item is out of stock';
  }

  return null;
}

export function useErrorMessageToasts() {
  const { toast } = useToast();

  return (e: unknown) => {
    const error = errorSchema.safeParse(e);
    const message =
      error.data === undefined ? null : getToastMessage(error.data);

    if (message !== null) {
      toast(<Toast variant={ToastVariant.Critical}>{message}</Toast>);
      return;
    }

    toast(
      <Toast variant={ToastVariant.Critical}>
        <Warning />
        Uh oh, something went wrong. Please try again.
      </Toast>
    );
    logUnknownErrorResponse('Failed to add to cart', {
      key: 'useErrorMessageToasts',
      error: JSON.stringify(e, null, 2),
    });
  };
}
